/**
 * PaymentModal.js — Record a worker advance payment (payout)
 *
 * Used in two places:
 *   - HomePage → "Pay advance" from the worker context menu
 *   - ViewWorkerPage → "Pay" button in the topbar
 *
 * On save, creates a record in the Directus `payouts` collection.
 * The `current_balance` on the worker is updated by a Directus trigger, not here.
 *
 * Props:
 *   open       {boolean}           — controls modal visibility
 *   onClose    {() => void}        — called when modal should close
 *   workerId   {string|number}     — Directus worker item ID
 *   workerName {string}            — displayed in the form header
 *   onSaved    {() => void}        — called after successful save (to refresh parent)
 */

import React, { useState } from 'react';
import { Modal, FormGroup, showAlert } from './UI';
import { addPayout } from '../api/directus';
import { playSuccessSound, playErrorSound } from '../utils/audio';
import { toISODate } from '../utils/helpers';

export default function PaymentModal({ open, onClose, workerId, workerName, onSaved }) {
  const [amount, setAmount] = useState('');
  const [date, setDate] = useState(toISODate(new Date()));
  const [mode, setMode] = useState('upi');
  const [remarks, setRemarks] = useState('');
  const [saving, setSaving] = useState(false);

  /** Reset form to defaults — called on close and after a successful save. */
  function reset() {
    setAmount(''); setDate(toISODate(new Date())); setMode('upi'); setRemarks('');
  }

  async function handleSubmit() {
    if (!amount || !workerId) return;
    setSaving(true);
    try {
      await addPayout({ worker: workerId, date, mode, amount: parseFloat(amount), remarks });
      showAlert('Payment recorded', 'success');
      playSuccessSound();
      reset();
      onSaved?.();
      onClose();
    } catch (e) {
      showAlert(e?.errors?.[0]?.message || 'Failed to record payment', 'error');
      playErrorSound();
    } finally { setSaving(false); }
  }

  return (
    <Modal
      open={open}
      onClose={() => { reset(); onClose(); }}
      title="Add Payment"
      size="modal-sm"
      footer={
        <>
          <button className="btn btn-ghost" onClick={() => { reset(); onClose(); }}>Close</button>
          <button className="btn btn-primary" onClick={handleSubmit} disabled={saving || !amount}>
            Add Payment
          </button>
        </>
      }
    >
      {workerName && (
        <div style={{ marginBottom: 14, fontSize: 13, color: '#64748b' }}>
          Worker: <strong style={{ color: '#0f172a' }}>{workerName}</strong>
        </div>
      )}

      <FormGroup label="Amount (₹)">
        <input className="form-input" type="number" value={amount} onChange={e => setAmount(e.target.value)} placeholder="0" />
      </FormGroup>

      <FormGroup label="Date">
        <input className="form-input" type="date" value={date} onChange={e => setDate(e.target.value)} />
      </FormGroup>

      {/* Payment mode — radio buttons instead of select for fewer taps on mobile */}
      <FormGroup label="Mode">
        <div style={{ display: 'flex', gap: 12, marginTop: 4 }}>
          {[['upi', 'UPI'], ['bank', 'Bank Transfer'], ['cash', 'Cash']].map(([val, lbl]) => (
            <label key={val} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
              <input type="radio" value={val} checked={mode === val} onChange={() => setMode(val)} />
              {lbl}
            </label>
          ))}
        </div>
      </FormGroup>

      <FormGroup label="Remarks">
        <input className="form-input" value={remarks} onChange={e => setRemarks(e.target.value)} placeholder="Optional" />
      </FormGroup>
    </Modal>
  );
}
